import {
  FlagProvider as UnleashFlagProvider,
  useUnleashClient
} from '@unleash/proxy-client-react'
import type { IToggle } from '@unleash/proxy-client-react'
import { useEffect, useMemo } from 'react'
import type { PropsWithChildren } from 'react'

import { createUnleashConfig, isUnleashConfigured } from './config'

type Props = PropsWithChildren<{
  /** Unleash app name, attached to metrics so flag usage is attributable. */
  appName: string
  /**
   * Bootstrap toggles for the flags this React root uses (build with `toggle`).
   * Served before the client reaches the server and as the outage fallback.
   * Pass a stable (module-level) array so the client isn't rebuilt each render.
   */
  bootstrap?: IToggle[]
}>

/**
 * Surfaces Unleash fetch failures/recoveries instead of letting the client
 * silently fall back to bootstrap, so a degraded flag state is observable.
 * Rendered inside the provider so the client is available.
 */
const FlagFetchLogger = () => {
  const client = useUnleashClient()

  useEffect(() => {
    if (!client) {
      return
    }

    // A degraded flag state should be observable rather than silent, so this
    // logger is a deliberate exception to the project-wide no-console rule.
    const onError = (error: unknown) =>
      // eslint-disable-next-line no-console
      console.error(
        '[feature-flags] Unleash fetch failed; serving bootstrap/last-known values',
        error
      )
    const onRecovered = () =>
      // eslint-disable-next-line no-console
      console.warn('[feature-flags] Unleash fetch recovered')

    client.on('error', onError)
    client.on('recovered', onRecovered)

    return () => {
      client.off('error', onError)
      client.off('recovered', onRecovered)
    }
  }, [client])

  return null
}

/**
 * App-level Unleash provider. Mount ONCE, in `App.tsx`, wrapping the tree.
 *
 * Safe to render even when the frontend API is unconfigured: the client is then
 * not started and `useFeatureFlag` serves the bootstrap/fallback defaults.
 */
export const FlagProvider = ({ appName, bootstrap, children }: Props) => {
  const config = useMemo(
    () => createUnleashConfig(appName, bootstrap),
    [appName, bootstrap]
  )

  return (
    <UnleashFlagProvider config={config} startClient={isUnleashConfigured}>
      <FlagFetchLogger />
      {children}
    </UnleashFlagProvider>
  )
}
